/**
 * Outcome Contracts — the machine-checkable BEFORE-claim an agent action
 * carries into the world.
 *
 * Every governed proposal already says WHAT it will write (types.ts) and WHY
 * it is justified (provenance.ts). An OutcomeContract adds the third leg no
 * competitor closes: what the action PREDICTS will happen, over what window,
 * and how many dollars ride on it. The contract is sealed alongside the
 * action; later the verifier (outcomeVerifier.ts) replays reality against it
 * and renders a verdict that feeds the Earn-Up trust engine.
 *
 * Deliberately pure: plain data in, plain data out. No store, no firebase,
 * no clocks read implicitly — `now` is always passed in by the caller.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** The measurable claim kinds a contract can stake. */
export type PredicateKind =
  | 'reply_received'
  | 'stage_advanced'
  | 'ticket_closed'
  | 'health_delta';

export const PREDICATE_KINDS: ReadonlyArray<PredicateKind> = [
  'reply_received',
  'stage_advanced',
  'ticket_closed',
  'health_delta',
];

/** Epoch-ms measurement window the claim must land inside. */
export type PredicateWindow = { start: number; end: number };

/** The machine-checkable predicate: kind + window (+ target for deltas). */
export type OutcomePredicate = {
  kind: PredicateKind;
  window: PredicateWindow;
  /** Required for 'health_delta': minimum measured delta to count as VERIFIED. */
  target?: number;
};

/** The sealed before-claim attached to one governed proposal. */
export type OutcomeContract = {
  /** The proposal this contract stakes an outcome on. */
  proposalId: string;
  /** The action class (e.g. 'schedule_follow_up') the claim rides on. */
  actionType: string;
  predicate: OutcomePredicate;
  /** USD staked on the claim — influenced, never banked. */
  dollarsAtStake: number;
};

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/** Default measurement window: 7 days from contract creation. */
export const DEFAULT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Minimal proposal shape the builder needs — any ProposedAction satisfies it. */
export type ContractProposalInput = {
  id: string;
  action: { kind: string; value?: number };
};

export type BuildContractOpts = {
  /** What the action claims will happen. */
  kind: PredicateKind;
  /** Contract creation time (epoch ms) — the window opens here. */
  now: number;
  /** Window length; defaults to {@link DEFAULT_WINDOW_MS}. */
  windowMs?: number;
  /** Required threshold for 'health_delta' claims. */
  target?: number;
  /** Override the dollars staked; defaults to the action's carried value. */
  dollarsAtStake?: number;
};

/**
 * Compile a proposal + claim options into an OutcomeContract. Pure and
 * deterministic: same inputs → same contract. Dollars default to the value
 * the action genuinely carries (never fabricated); a valueless action stakes 0.
 */
export function buildOutcomeContract(
  proposal: ContractProposalInput,
  opts: BuildContractOpts,
): OutcomeContract {
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const predicate: OutcomePredicate = {
    kind: opts.kind,
    window: { start: opts.now, end: opts.now + windowMs },
  };
  if (opts.target !== undefined) predicate.target = opts.target;
  return {
    proposalId: proposal.id,
    actionType: proposal.action.kind,
    predicate,
    dollarsAtStake: opts.dollarsAtStake ?? proposal.action.value ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Machine-check a contract before it is sealed. Returns every problem found
 * (not just the first) so a confirm card can show exactly what the claim is
 * missing. A contract that fails validation must never be sealed or verified.
 */
export function validateOutcomeContract(c: unknown): { ok: boolean; errors: string[] } {
  const errors: string[] = [];

  if (typeof c !== 'object' || c === null) {
    return { ok: false, errors: ['contract must be an object'] };
  }
  const o = c as Record<string, unknown>;

  if (typeof o.proposalId !== 'string' || o.proposalId.trim() === '') {
    errors.push('proposalId must be a non-empty string');
  }
  if (typeof o.actionType !== 'string' || o.actionType.trim() === '') {
    errors.push('actionType must be a non-empty string');
  }

  const p = o.predicate as Record<string, unknown> | undefined;
  if (typeof p !== 'object' || p === null) {
    errors.push('predicate must be an object');
  } else {
    if (
      typeof p.kind !== 'string' ||
      !PREDICATE_KINDS.includes(p.kind as PredicateKind)
    ) {
      errors.push('predicate kind is invalid');
    }
    const w = p.window as Record<string, unknown> | undefined;
    if (typeof w !== 'object' || w === null) {
      errors.push('predicate window is missing');
    } else {
      const start = w.start;
      const end = w.end;
      if (typeof start !== 'number' || !Number.isFinite(start)) {
        errors.push('window start must be a finite number');
      }
      if (typeof end !== 'number' || !Number.isFinite(end)) {
        errors.push('window end must be a finite number');
      }
      if (
        typeof start === 'number' &&
        typeof end === 'number' &&
        Number.isFinite(start) &&
        Number.isFinite(end) &&
        end <= start
      ) {
        errors.push('window end must be after window start');
      }
    }
    if (p.kind === 'health_delta') {
      if (typeof p.target !== 'number' || !Number.isFinite(p.target)) {
        errors.push('health_delta predicate requires a finite numeric target');
      }
    }
  }

  const dollars = o.dollarsAtStake;
  if (typeof dollars !== 'number' || !Number.isFinite(dollars) || dollars < 0) {
    errors.push('dollarsAtStake must be a finite number >= 0');
  }

  return { ok: errors.length === 0, errors };
}
